import { formatCountdown } from '@/utils/timer'
import { resetInTrueUTC, resetInViewerTime, viewerIsUTC } from '@/utils/reset-time'

/**
 * The "reset time" tag — the shared shell behind every reset chip on this dashboard.
 *
 * Deliberately the SAME UI as admin-dashboard's `components/cap/ResetTimeTag.tsx`: same layout
 * (caption · viewer time · UTC · divider · countdown), same type sizes, same amber pill. Admins
 * and affiliates talk to each other about these numbers, so a boundary that looks like one thing
 * on `/cap` and another on the portal costs a support round-trip every time.
 *
 * It shows the SAME instant two ways, because either alone misleads:
 *
 *   - the VIEWER's own clock, auto-detected from the browser, which is what you act on;
 *   - TRUE UTC, the only form two people in different countries can quote to each other — and the
 *     form the cap manager, Everflow and every support thread use.
 *
 * There is deliberately no timezone picker. A picker makes the reader pick a frame of reference
 * before they can read a number, and gets it wrong the first time someone travels.
 *
 * ⚠️ Colors go through `brand-yellow`, NOT through Tailwind's `dark:` variant, and that is
 * deliberate. This app ships both themes by toggling `.dark` on `<html>` (`useDarkMode`) and
 * overriding CSS variables under it — but it never wired Tailwind's `dark:` variant to that class,
 * so `dark:bg-amber-500/10` compiles to a **bare, ungated class selector** (verified in the built
 * CSS: no `:is(.dark)` wrapper, no media query) and does not track the theme. Sibling chips that
 * use `dark:` have the same latent problem; fixing that variant app-wide is its own change.
 * `--color-brand-yellow` and `--color-text-muted` ARE redefined under `.dark`, so an alpha over
 * them adapts correctly in both themes with nothing to keep in sync — the same "one alpha amber"
 * shape admin's copy uses.
 *
 * No SSR guard is needed here (unlike admin's, which gates on `mounted`): this is a Vite SPA, so
 * `Intl` resolves to the real viewer's zone on first render — there is no server render to
 * disagree with.
 */
export function ResetTimeTag({
  resetAt,
  countdownMs,
  showCountdown = true,
  compact = false,
  title,
  className = '',
}: {
  /** The instant this offer's cap day rolls over. */
  resetAt: Date
  /**
   * Milliseconds left, when the caller already tracks it (the header ticks once a second for the
   * whole page). Omitted, the countdown is derived from `resetAt` at render time.
   */
  countdownMs?: number
  /** Hide the ticking clock where only the boundary matters — e.g. a dense offer tab. */
  showCountdown?: boolean
  /** Drop the "Resets" caption, for inline use beside a title. */
  compact?: boolean
  title?: string
  className?: string
}) {
  const remaining = countdownMs ?? Math.max(0, resetAt.getTime() - Date.now())

  return (
    <span
      className={
        'inline-flex items-center gap-2 rounded-lg border border-brand-yellow/30 bg-brand-yellow/10 ' +
        (compact ? 'px-2 py-0.5 ' : 'px-2.5 py-1 ') +
        className
      }
      title={title}
    >
      {!compact && (
        <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Resets
        </span>
      )}
      <span className="text-xs font-medium tabular-nums text-brand-yellow">
        {resetInViewerTime(resetAt)}
      </span>
      {/* Suppressed for a viewer already on UTC — the line above would otherwise print the same
          instant twice, which reads as a bug rather than as two frames of reference. */}
      {!viewerIsUTC(resetAt) && (
        <span className="text-[10px] tabular-nums text-text-muted">{resetInTrueUTC(resetAt)}</span>
      )}
      {showCountdown && (
        <span className="border-l border-brand-yellow/30 pl-2 font-mono text-xs font-semibold tabular-nums text-brand-yellow">
          {formatCountdown(remaining)}
        </span>
      )}
    </span>
  )
}
